import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button, Input, Badge, Modal, Table } from '@/components/ui';
import type { Column } from '@/components/ui';
import { tenantsService } from '@/services/tenants.service';
import type {
  TenantDetail as TenantDetailType,
  TenantPlan,
  TenantStatus,
  UpdateTenantRequest,
  TenantUser,
} from '@/types';

const PLANS: { value: TenantPlan; label: string }[] = [
  { value: 'FREE', label: 'Free' },
  { value: 'BASIC', label: 'Basic' },
  { value: 'PROFESSIONAL', label: 'Professional' },
  { value: 'ENTERPRISE', label: 'Enterprise' },
];

const getPlanBadgeVariant = (plan: TenantPlan) => {
  switch (plan) {
    case 'ENTERPRISE':
      return 'info';
    case 'PROFESSIONAL':
      return 'success';
    case 'BASIC':
      return 'warning';
    default:
      return 'default';
  }
};

const getStatusBadgeVariant = (status: TenantStatus) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'SUSPENDED':
      return 'danger';
    case 'PENDING':
      return 'warning';
    default:
      return 'default';
  }
};

export const TenantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<TenantDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdateTenantRequest>({});
  const [isSaving, setIsSaving] = useState(false);

  // Modal states
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchTenant = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const data = await tenantsService.getTenantById(id);
        setTenant(data);
        setEditForm({
          name: data.name,
          email: data.email,
          phone: data.phone || '',
          address: data.address || '',
          plan: data.plan,
          maxUsers: data.maxUsers,
        });
      } catch {
        setError('Failed to load tenant details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenant();
  }, [id]);

  const handleSave = async () => {
    if (!id) return;

    setIsSaving(true);
    try {
      const updated = await tenantsService.updateTenant(id, editForm);
      setTenant(updated);
      setIsEditing(false);
    } catch {
      setError('Failed to update tenant');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!id || !tenant) return;

    setIsProcessing(true);
    try {
      const updated =
        tenant.status === 'ACTIVE'
          ? await tenantsService.suspendTenant(id)
          : await tenantsService.activateTenant(id);
      setTenant(updated);
      setIsSuspendModalOpen(false);
    } catch {
      setError('Failed to update tenant status');
    } finally {
      setIsProcessing(false);
    }
  };

  const userColumns: Column<TenantUser>[] = [
    {
      key: 'name',
      header: 'User',
      render: (user) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-xs font-medium text-primary-700">
              {user.firstName.charAt(0)}
              {user.lastName.charAt(0)}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-secondary-900">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs text-secondary-500">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user) => <Badge variant="info">{user.role}</Badge>,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (user) => (
        <Badge variant={user.isActive ? 'success' : 'danger'} dot>
          {user.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (user) => (
        <Link
          to={`/users/${user.id}`}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          View
        </Link>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-secondary-200 rounded mb-6"></div>
          <div className="h-64 bg-secondary-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="space-y-6">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-700">{error || 'Tenant not found'}</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/tenants')}>
          Back to Tenants
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Link
            to="/tenants"
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5 text-secondary-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-secondary-900">
                {tenant.name}
              </h1>
              <Badge variant={getPlanBadgeVariant(tenant.plan)}>{tenant.plan}</Badge>
              <Badge variant={getStatusBadgeVariant(tenant.status)} dot>
                {tenant.status}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-secondary-500">{tenant.slug}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {!isEditing ? (
            <>
              <Button variant="secondary" onClick={() => setIsEditing(true)}>
                Edit Tenant
              </Button>
              <Button
                variant={tenant.status === 'ACTIVE' ? 'danger' : 'primary'}
                onClick={() => setIsSuspendModalOpen(true)}
              >
                {tenant.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} isLoading={isSaving}>
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-secondary-900">
                Tenant Information
              </h2>
            </Card.Header>
            <Card.Body>
              {isEditing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Name"
                    value={editForm.name || ''}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={editForm.email || ''}
                    onChange={(e) =>
                      setEditForm({ ...editForm, email: e.target.value })
                    }
                  />
                  <Input
                    label="Phone"
                    value={editForm.phone || ''}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phone: e.target.value })
                    }
                  />
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                      Plan
                    </label>
                    <select
                      value={editForm.plan || ''}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          plan: e.target.value as TenantPlan,
                        })
                      }
                      className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {PLANS.map((plan) => (
                        <option key={plan.value} value={plan.value}>
                          {plan.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Max Users"
                    type="number"
                    value={editForm.maxUsers?.toString() || ''}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        maxUsers: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                  <div className="sm:col-span-2">
                    <Input
                      label="Address"
                      value={editForm.address || ''}
                      onChange={(e) =>
                        setEditForm({ ...editForm, address: e.target.value })
                      }
                    />
                  </div>
                </div>
              ) : (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Name
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {tenant.name}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Email
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {tenant.email}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Phone
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {tenant.phone || 'Not provided'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Address
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {tenant.address || 'Not provided'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Users
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {tenant.userCount} / {tenant.maxUsers}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">
                      Created
                    </dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {new Date(tenant.createdAt).toLocaleDateString()}
                    </dd>
                  </div>
                </dl>
              )}
            </Card.Body>
          </Card>

          {/* Users List */}
          <Card padding="none">
            <div className="px-6 py-4 border-b border-secondary-200">
              <h2 className="text-lg font-semibold text-secondary-900">
                Users ({tenant.users.length})
              </h2>
            </div>
            <Table
              columns={userColumns}
              data={tenant.users}
              keyExtractor={(user) => user.id}
              emptyMessage="No users in this tenant"
            />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Wallet */}
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-secondary-900">
                Wallet Balance
              </h2>
            </Card.Header>
            <Card.Body>
              <p className="text-3xl font-bold text-secondary-900">
                ${tenant.walletBalance.toFixed(2)}
              </p>
            </Card.Body>
          </Card>

          {/* Owner Info */}
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-secondary-900">Owner</h2>
            </Card.Header>
            <Card.Body>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-700">
                    {tenant.owner.firstName.charAt(0)}
                    {tenant.owner.lastName.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary-900">
                    {tenant.owner.firstName} {tenant.owner.lastName}
                  </p>
                  <p className="text-xs text-secondary-500">{tenant.owner.email}</p>
                </div>
              </div>
              <Link
                to={`/users/${tenant.owner.id}`}
                className="mt-3 block text-sm text-primary-600 hover:text-primary-700"
              >
                View Owner Profile
              </Link>
            </Card.Body>
          </Card>

          {/* Settings */}
          <Card>
            <Card.Header>
              <h2 className="text-lg font-semibold text-secondary-900">
                Settings
              </h2>
            </Card.Header>
            <Card.Body className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600">Public Bookings</span>
                <Badge
                  variant={
                    tenant.settings.allowPublicBookings ? 'success' : 'default'
                  }
                >
                  {tenant.settings.allowPublicBookings ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600">Require Approval</span>
                <Badge
                  variant={tenant.settings.requireApproval ? 'warning' : 'default'}
                >
                  {tenant.settings.requireApproval ? 'Yes' : 'No'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600">Timezone</span>
                <span className="text-sm text-secondary-900">
                  {tenant.settings.timezone}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600">Currency</span>
                <span className="text-sm text-secondary-900">
                  {tenant.settings.currency}
                </span>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Suspend/Activate Modal */}
      <Modal
        isOpen={isSuspendModalOpen}
        onClose={() => setIsSuspendModalOpen(false)}
        title={tenant.status === 'ACTIVE' ? 'Suspend Tenant' : 'Activate Tenant'}
        size="sm"
      >
        <Modal.Body>
          <p className="text-secondary-600">
            Are you sure you want to{' '}
            {tenant.status === 'ACTIVE' ? 'suspend' : 'activate'}{' '}
            <span className="font-medium text-secondary-900">{tenant.name}</span>?
          </p>
          {tenant.status === 'ACTIVE' && (
            <p className="mt-2 text-sm text-secondary-500">
              All {tenant.userCount} users in this tenant will lose access to the
              platform.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsSuspendModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant={tenant.status === 'ACTIVE' ? 'danger' : 'primary'}
            onClick={handleToggleStatus}
            isLoading={isProcessing}
          >
            {tenant.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TenantDetail;
